"use client";

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

import { useLocalStorage } from "@uidotdev/usehooks";

import { LocalStorageKeys } from "@/shared/enums/local-storage-keys.enum";

import { toValidMapStyle } from "../utils/to-valid-map-style";

import type { MapStyle } from "../enums/map-style.enum";

interface MapStyleContextValue {
  mapStyle: Record<string, MapStyle>;
  setMapStyle: (districtId: string, style: MapStyle) => void;
  setStoredForPreset: (stored: Record<string, string>) => void;
}

const MapStyleContext = createContext<MapStyleContextValue | undefined>(undefined);

const emptyByDistrict = {} as Record<string, MapStyle>;

interface MapStyleProviderProps extends PropsWithChildren {
  /** When false, style is session-only (not written to localStorage). */
  persist?: boolean;
}

export const MapStyleProvider: FC<MapStyleProviderProps> = ({ children, persist = true }) => {
  const [persistentStored, setPersistentStored] = useLocalStorage<Record<string, string> | null>(
    LocalStorageKeys.MapStyle,
    null,
  );
  const [ephemeralStored, setEphemeralStored] = useState<Record<string, string> | null>(null);
  const stored = persist ? persistentStored : ephemeralStored;
  const setStored = persist ? setPersistentStored : setEphemeralStored;

  const mapStyle = useMemo((): Record<string, MapStyle> => {
    const raw = stored ?? emptyByDistrict;
    return Object.entries(raw).reduce<Record<string, MapStyle>>(
      (acc, [id, s]) => {
        acc[id] = toValidMapStyle(s);
        return acc;
      },
      {},
    );
  }, [stored]);

  const setMapStyle = useCallback(
    (districtId: string, style: MapStyle) => {
      setStored((prev) => ({ ...(prev ?? {}), [districtId]: style }));
    },
    [setStored],
  );

  const setStoredForPreset = useCallback(
    (nextStored: Record<string, string>) => {
      setStored(Object.keys(nextStored).length > 0 ? nextStored : null);
    },
    [setStored],
  );

  const value = useMemo(
    () => ({ mapStyle, setMapStyle, setStoredForPreset }),
    [mapStyle, setMapStyle, setStoredForPreset],
  );

  return (
    <MapStyleContext.Provider value={value}>
      {children}
    </MapStyleContext.Provider>
  );
};

export function useMapStyle(): MapStyleContextValue {
  const context = useContext(MapStyleContext);
  if (context === undefined) {
    throw new Error("useMapStyle must be used within a MapStyleProvider");
  }

  return context;
}
